import { HorizontalDivider, Icon, Typography } from '@smartgift/frontend-toolkit';
import difference from 'lodash/difference';
import React, { Fragment, useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ADD_TAG_STEPS } from '@hero/components/modals/AddTagModal/constant';
import { useGetContacts } from '@hero/services/contact-service/hooks';

import * as S from './style';

function AddTagReviewAndSubmit({
  selectedRows,
  tagRows,
  setFooterProps,
  setStep,
  setModalHeader,
  onUpdate
}) {
  const { t } = useTranslation();
  const [isUpdateLoading, setIsUpdateLoading] = useState(false);

  const tagRowsArray = useMemo(() => Object.keys(tagRows), [tagRows]);
  const selectedRowsArray = useMemo(() => Object.keys(selectedRows), [selectedRows]);

  const additionalTags = useMemo(() => {
    return difference(selectedRowsArray, tagRowsArray);
  }, [tagRowsArray, selectedRowsArray]);

  const { data: contacts } = useGetContacts(
    {
      filters: {
        tags: selectedRowsArray
      }
    },
    {
      enabled: !!selectedRowsArray?.length > 0
    }
  );

  const table = useMemo(() => {
    const newTagTotal = {
      key: t('newTagTotal'),
      value: selectedRowsArray.length,
      isActive: true
    };
    const addedTags = {
      key: t('addedTags'),
      value: additionalTags.length,
      subTitle: t('contacts'),
      subValue: contacts?.docs?.length
    };
    const currentTags = {
      key: t('currentTags'),
      value: tagRowsArray.length
    };

    return [newTagTotal, addedTags, currentTags];
  }, [contacts]);

  const handleUpdate = async () => {
    setIsUpdateLoading(true);
    await onUpdate(selectedRows);
  };

  useEffect(() => {
    setFooterProps({
      leftButtonText: t('back'),
      leftButtonOnClick: () => setStep(ADD_TAG_STEPS.ADD_TAG_TABLE),
      isLeftButtonActive: true,
      isRightButtonActive: true,
      rightButtonOnClick: handleUpdate,
      isRightButtonLoading: isUpdateLoading,
      rightButtonText: t('updateTagList')
    });
    setModalHeader({
      title: t('reviewAndSubmit'),
      icon: <Icon name="left-chevron" />,
      onBack: () => setStep(ADD_TAG_STEPS.ADD_TAG_TABLE)
    });
  }, [isUpdateLoading]);

  return (
    <S.ReviewAndConfirm>
      <Typography variant="body2" color="neutral" emphasis="500">
        {t('reviewAndConfirmDescription')}
      </Typography>
      <S.ReviewBoxWrapper>
        <Icon name="tag" style={{ minWidth: 80, width: 80, height: 80, minHeight: 80 }} />
        <S.ReviewBox>
          {table.map((row, index) => {
            return (
              <Fragment key={row.key}>
                <S.ReviewBox>
                  <S.ReviewBoxRow isActive={row?.isActive}>
                    <Typography variant={row?.isActive ? 'body1Bold' : 'body2'}>
                      {row.key}:
                    </Typography>
                    <Typography variant={row?.isActive ? 'body1Bold' : 'body2'}>
                      {row.value}
                    </Typography>
                  </S.ReviewBoxRow>
                  {row?.subTitle && (
                    <S.ReviewBoxSubRow>
                      <Typography variant="caption1" emphasis="weak">
                        {row.subTitle}:
                      </Typography>
                      <Typography variant="caption1Bold" emphasis="weak">
                        {row.subValue}
                      </Typography>
                    </S.ReviewBoxSubRow>
                  )}
                  {index !== table.length - 1 && !row.isActive && (
                    <HorizontalDivider $margin="10px 0 0 0" />
                  )}
                </S.ReviewBox>
              </Fragment>
            );
          })}
        </S.ReviewBox>
      </S.ReviewBoxWrapper>
    </S.ReviewAndConfirm>
  );
}

export { AddTagReviewAndSubmit };
